import {isAxiosError} from 'axios';
import clsx from 'clsx';
import copy from 'copy-to-clipboard';
import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useShallow} from 'zustand/react/shallow';
import {Body, Headline, Icon, IconName} from '@lightricks/react-design-system';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {
  EVENT_NAMES,
  FLOW_NAMES,
  SCREEN_NAMES,
} from '@/lib/analytics/analyticsConstants';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import PERMISSIONS from '@/config/permissions';
import {ROUTES} from '@/config/routesConstants';
import {MembershipLabel} from '@/types/creatorProfile';
import {CreatorSearchResult} from '@/types/models/creator-crm/creatorCrm';
import {
  CreatorCrmStateDefinition,
  TableColumnConfig,
  ViewType,
} from '@/types/models/creator-crm/creatorCrmStore';
import {
  BaseFilterOption,
  FiltersGroup,
  FiltersState,
  SingleSelection,
} from '@/types/models/creator-crm/filter';
import {FilterIds, FiltersGroupId} from '@/types/models/creator-crm/filterId';
import {SortingState} from '@/types/models/creator-crm/sorting';
import {Segment} from '@/types/models/segments/segment';
import CreatorAction from '@/views/creators/components/creator-action';
import {
  ActiveActionKeyAndCreators,
  CREATOR_ACTION_KEYS,
  CreatorActionKey,
} from '@/views/creators/components/creator-action/CreatorAction';
import {CreatorProps} from '@/views/creators/components/creator-action/action/ActionProps';
import {ActionItem} from '@/views/creators/components/creators-actions-bar/ActionsBar';
import CreatorsActionsBar from '@/views/creators/components/creators-actions-bar/CreatorsActionsBar';
import NoActiveCampaignsModal from '@/views/creators/components/no-active-campaigns-modal';
import ActionsDrawer from '@/views/creators/creator-crm/components/actions-drawer';
import ActiveFiltersBar, {
  type DisplayItem,
  groupedDisplayItemsFromFilters,
  groupedDisplayItemsFromSearchState,
} from '@/views/creators/creator-crm/components/active-filters-bar';
import useActiveFiltersBar from '@/views/creators/creator-crm/components/active-filters-bar/useActiveFiltersBar';
import AddNoteModal from '@/views/creators/creator-crm/components/add-note-modal';
import SearchControls from '@/views/creators/creator-crm/components/search-controls';
import SearchCreatorsDisplay from '@/views/creators/creator-crm/components/search-creators-display';
import SelectionBanner from '@/views/creators/creator-crm/components/selection-banner';
import {BULK_ACTION_LIMIT} from '@/views/creators/creator-crm/constants';
import useCreatorTableColumnsConfig from '@/views/creators/creator-crm/hooks/use-creator-table-columns-config';
import useCreatorsTableColumns, {
  PAGE_SELECTION_STATE,
  PageSelectionState,
} from '@/views/creators/creator-crm/hooks/use-creators-table-columns';
import useSegments from '@/views/creators/creator-crm/hooks/use-segments';
import getAnalyticsSearchScreenName from '@/views/creators/creator-crm/utils/getAnalyticsSearchScreenName';
import getObjectDiff from '@/views/creators/creator-crm/utils/getObjectDiff';
import getProfileImageUrl from '@/views/creators/creator-crm/utils/getProfileImageUrl';
import mapCreatorCardPropsForAction from '@/views/creators/creator-crm/utils/mapCreatorCardPropsForAction';
import mapCreatorSearchResultsForAction from '@/views/creators/creator-crm/utils/mapCreatorSearchResultsForAction';
import mapCreatorsForDisplay from '@/views/creators/creator-crm/utils/mapCreatorsForDisplay';
import {
  disabledActionKeysForCreator,
  isOffPlatformBulkGatedAction,
  isOffPlatformCreator,
  shouldGateOffPlatformBulkActions,
} from '@/views/creators/creator-crm/utils/offPlatformCreator';
import {
  mapCreatorCrmStateToPersistenceState,
  mapPersistenceStateToCreatorCrmState,
} from '@/views/creators/creator-crm/utils/persistenceState';
import ColumnSelector from '@/components/column-selector';
import {CreatorCardProps} from '@/components/creator-card/CreatorCard';
import {CreatorMenuItemProps} from '@/components/creator-card/CreatorOverflowMenuButton';
import FloatingPopperBar from '@/components/floating-popper-bar';
// Local creator-crm components
import MessageCreatorsModal, {
  MessageCreator,
} from '@/components/message-creators-modal';
import NotificationBanner, {
  NotificationBannerState,
  NotificationContentType,
} from '@/components/notification-banner';
import Page from '@/components/page';
import SegmentManagementModal from '@/components/segment-management-modal';
import useTrackRecruitmentActionFlowEvent from '@/hooks/analytics/use-track-recruitment-action-flow-event';
import useTrackRecruitmentResultsPresented from '@/hooks/analytics/use-track-recruitment-results-presented';
import useTrackRecruitmentSearchDetail from '@/hooks/analytics/use-track-recruitment-search-detail';
import useTrackRecruitmentSearchFlowStarted from '@/hooks/analytics/use-track-recruitment-search-flow-started';
import useTrackSearchButtonEvent from '@/hooks/analytics/use-track-search-button-event';
import useTrackSearchScreenEvent from '@/hooks/analytics/use-track-search-screen-event';
import {
  useBulkThreads,
  useBulkThreadsAll,
} from '@/hooks/mutations/creators/use-bulk-threads';
import useRecruitToCrm, {
  RecruitToCrmProfileResultItem,
  useRecruitAllToCrm,
} from '@/hooks/mutations/creators/use-recruit-to-crm';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useBrandCampaignsQuery from '@/hooks/queries/use-brand-campaigns-query';
import useBrandCreatorGroupsQuery from '@/hooks/queries/use-brand-creator-groups-query';
import useCreatorGroupsLabelsQuery from '@/hooks/queries/use-creator-groups-labels-query';
import useCreatorProfileQuery from '@/hooks/queries/use-creator-profile-query';
import useCreatorsMetadataQuery from '@/hooks/queries/use-creators-metadata-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useSearchCreatorCrmQuery from '@/hooks/queries/use-search-creator-crm-query';
import useSubscriptionQuery from '@/hooks/queries/use-subscription-query';
import useBrandId from '@/hooks/use-brand-id';
import useDashboardUserPermissions from '@/hooks/use-dashboard-user-permissions';
import useMessagingPanel from '@/hooks/use-messaging-panel';
import useNavigation from '@/hooks/use-navigation';
import usePrevious from '@/hooks/use-previous';
import useRaiseCreatorAddedToListSuccess from '@/hooks/use-raise-creators-added-to-list-success';
import useScrollDirection, {
  SCROLL_DIRECTION,
} from '@/hooks/use-scroll-direction';
// Use the new creator CRM store
import useCreatorCrmStore, {
  creatorCrmActions,
  initialCreatorCrmState,
  VIEW_TYPE_TABLE,
} from '@/stores/creator-crm/creatorCrmStore';
import styles from './CreatorCrm.module.scss';
import stickyStyles from './creator-crm-table-sticky.module.scss';

const TRANSLATION_PREFIX = 'views.creators';
const PERSISTED_STATE_KEY = 'ps';

function usePrefetchRequiredData(brandId: string) {
  const {
    creatorGroups,
    isLoading: isLoadingBrandCreatorGroup,
    isFetched: isFetchedBrandCreatorGroup,
  } = useBrandCreatorGroupsQuery({
    brandId,
    refetchOnWindowFocus: false,
  });
  const creatorGroupId = creatorGroups[0]?.id ?? '';
  const {
    labels,
    isLoading: isLoadingCreatorGroupsLabels,
    isFetched: isFetchedCreatorGroupsLabels,
  } = useCreatorGroupsLabelsQuery({
    creatorGroupId,
    refetchOnWindowFocus: false,
  });

  return {
    creatorGroups,
    labels,
    isLoadingPrefetchedData:
      isLoadingBrandCreatorGroup || isLoadingCreatorGroupsLabels,
    isFetchedPrefetchedData:
      isFetchedBrandCreatorGroup && isFetchedCreatorGroupsLabels,
  };
}

function getPersistedStateFromSearchParams(
  searchParams: URLSearchParams
): CreatorCrmStateDefinition | undefined {
  const persistedState = searchParams.get(PERSISTED_STATE_KEY) ?? '';
  if (!persistedState) {
    return undefined;
  }
  try {
    const decodedString = decodeURIComponent(escape(atob(persistedState)));
    return mapPersistenceStateToCreatorCrmState(
      decodedString,
      initialCreatorCrmState
    );
  } catch (e) {
    return undefined;
  }
}

function encodePersistedState(
  viewType: ViewType,
  filterGroups: Record<FiltersGroupId, FiltersGroup<FilterIds>>,
  sorting: SortingState | undefined,
  brandId: string,
  tableColumns?: TableColumnConfig[],
  filtersState?: FiltersState,
  selectedSegmentId?: string | null,
  isCrmMode?: boolean
): string {
  const jsonString = JSON.stringify(
    mapCreatorCrmStateToPersistenceState(
      viewType,
      filterGroups,
      sorting,
      brandId,
      tableColumns,
      filtersState,
      selectedSegmentId,
      isCrmMode
    ) ?? ''
  );
  return btoa(unescape(encodeURIComponent(jsonString)));
}

type ActiveModal =
  | {type: 'message'; creatorId: string | null}
  | {type: 'note'; creatorId: string}
  | null;

const RECRUIT_TO_CRM_ACTION = {
  icon: 'Navigation-Creator-Add' as IconName,
  bulkTooltipKey: 'creator-bulk-recruit-to-crm-tooltip',
  overflowMenuTitleKey: 'creator-card-overflow-menu-recruit-to-crm',
};

function CreatorCrm() {
  const flags = useFlags();
  const {
    data: metadata,
    isFetched: isMetadataFetched,
    isError: isMetadataError,
  } = useCreatorsMetadataQuery();
  const brandId = useBrandId();
  const {subscription} = useSubscriptionQuery({
    brandId,
  });
  const {dashboardUser} = useDashboardUserQuery({});
  const {data: agency, currentBrand} = useAgencyQuery({brandId});
  const agencyName = agency?.name ?? '';
  const dashboardUserPermissions = useDashboardUserPermissions(
    [PERMISSIONS.COLLABORATIONS.CREATE],
    dashboardUser?.permissions
  );
  const raiseCreatorAddedToListSuccess = useRaiseCreatorAddedToListSuccess();
  const canCreateCollaboration =
    !subscription.isFree && dashboardUserPermissions.createCollaborations;

  const trackSearchButtonEvent = useTrackSearchButtonEvent();
  const trackSearchScreenEvent = useTrackSearchScreenEvent();
  const trackRecruitmentActionFlowEvent = useTrackRecruitmentActionFlowEvent();
  const trackRecruitmentResultsPresented =
    useTrackRecruitmentResultsPresented();
  const trackRecruitmentSearchFlowStarted =
    useTrackRecruitmentSearchFlowStarted();
  const trackRecruitmentSearchDetail = useTrackRecruitmentSearchDetail();
  const {navigate} = useNavigation();

  const [searchParams, setSearchParams] = useSearchParams();
  const previousSearchParamsString = usePrevious(searchParams?.toString());
  const hasPersistedStateInSearchParams = searchParams.has(PERSISTED_STATE_KEY);
  const [isRestoringFromPersistenceState, setIsRestoringFromPersistenceState] =
    useState(hasPersistedStateInSearchParams);
  const [segmentModalOpen, setSegmentModalOpen] = useState(false);

  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(
    null
  );
  const scrollContainerRef = useMemo(
    () => ({current: scrollContainer}),
    [scrollContainer]
  );
  const scrollDirection = useScrollDirection(scrollContainerRef, {
    threshold: 100,
    hideDistance: 15,
    showDistance: 10,
    minUpdateIntervalMs: 150,
  });

  const shouldHideHeader = scrollDirection === SCROLL_DIRECTION.DOWN;

  const [creatorsInvitedStatus, setCreatorsInvitedStatus] = useState<
    {id: string; invited: boolean}[]
  >([]);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const singleCreatorActionId = activeModal?.creatorId ?? null;

  useEffect(() => {
    const recruitmentSearchFlowExists = AnalyticsService.flowExists(
      FLOW_NAMES.RECRUITMENT_SEARCH
    );
    const recruitmentSearchFlow = AnalyticsService.getOrCreateFlow(
      FLOW_NAMES.RECRUITMENT_SEARCH
    );

    if (!recruitmentSearchFlowExists) {
      trackRecruitmentSearchFlowStarted.started({
        flow_id: recruitmentSearchFlow.flow_id,
        screen_presentation_id: recruitmentSearchFlow.screen_presentation_id,
        recruitment_search_id: '',
        recruitment_screen_name: getAnalyticsSearchScreenName(viewType),
        campaign_id: '',
        num_results: '',
      });
    }
  }, [trackRecruitmentSearchFlowStarted]);

  const [
    viewType,
    filtersGroups,
    paging,
    sorting,
    selectedCreators,
    isAllFilterMatchingSelected,
    filtersGroupsSnapshot,
    filtersState,
    sessionId,
    selectedBrandId,
    selectedSegmentId,
    tableColumns,
    isSelectMode,
    isCrmMode,
  ] = useCreatorCrmStore(
    useShallow((state) => [
      state.viewType,
      state.content.filtersState.filtersGroups,
      state.content.paging,
      state.content.sorting,
      state.content.selectedCreators,
      state.content.isAllFilterMatchingSelected,
      state.content.filtersState.filtersGroupsSnapshot,
      state.content.filtersState,
      state.content.sessionId,
      state.selectedBrandId,
      state.selectedSegmentId,
      state.content.tableColumns,
      state.isSelectMode,
      state.isCrmMode,
    ])
  );

  // Reset scroll container when view type changes so the hook attaches to the new element
  useEffect(() => {
    setScrollContainer(null);
  }, [viewType]);

  const {searchState} = filtersState;
  const isSearchMode = flags.crmSearchToggle && !isCrmMode;

  const onSearchButtonClick = useCallback(
    ({
      button_name,
      tab,
      creator_id,
    }: {
      button_name: string;
      tab?: string;
      creator_id?: string;
    }) => {
      const tabValue = tab ?? (isSearchMode ? 'Search' : 'CRM');
      trackSearchButtonEvent.pressed({
        button_name,
        tab: tabValue,
        creator_id,
        screen_name: getAnalyticsSearchScreenName(viewType),
        campaign_id: JSON.stringify({searchSessionId: sessionId}),
        source: SCREEN_NAMES.CREATOR_CRM,
      });
    },
    [trackSearchButtonEvent, viewType, sessionId, isSearchMode]
  );

  const openSegmentManagementModal = useCallback(() => {
    setSegmentModalOpen(true);
    onSearchButtonClick({
      button_name: 'Save Filters',
    });
  }, [setSegmentModalOpen, onSearchButtonClick]);
  const closeSegmentManagementModal = useCallback(() => {
    setSegmentModalOpen(false);
  }, [setSegmentModalOpen]);

  const {
    segments,
    currentFiltersForSegment,
    activeSegment,
    activeCampaignSegment,
    canSaveSegment,
    isLoadingSegments,
    selectSegment,
  } = useSegments({
    merchantId: brandId ?? '',
    brandId: brandId ?? undefined,
    enabled: !!brandId,
    selectedSegmentId,
  });

  const wrappedSelectSegment = useCallback(
    async (segment: Segment) => {
      await selectSegment(segment);
      creatorCrmActions.setSelectedSegmentId(segment.id);
      onSearchButtonClick({
        button_name: `Saved Filter: ${segment.name}`,
      });
    },
    [selectSegment, onSearchButtonClick]
  );

  const wrappedSegmentSaved = useCallback(
    (segmentId: string, segment: Segment) => {
      const isNew = !segments.find((s) => s.id === segmentId);
      creatorCrmActions.setSelectedSegmentId(segmentId);
      creatorCrmActions.closeFiltersDrawer();

      trackRecruitmentSearchDetail.detail({
        recruitment_search_id: sessionId || '',
        recruitment_screen_name: getAnalyticsSearchScreenName(viewType),
        search_detail_id: `Save Filters: ${isNew ? 'Created' : 'Updated'}, ${segment.name}`,
        search_detail_name: segment.name,
        search_detail_value: JSON.stringify(segment),
      });
    },
    [sessionId, trackRecruitmentSearchDetail, viewType, segments]
  );

  const wrappedOnSegmentDeleted = useCallback(
    (segment: Segment) => {
      if (segment.id === activeSegment?.id) {
        creatorCrmActions.resetFiltersAndCloseDrawer();
      }
      onSearchButtonClick({
        button_name: `Delete Saved Filter: ${segment.name}`,
      });
    },
    [onSearchButtonClick, activeSegment?.id]
  );

  const onSegmentsButtonOpen = useCallback(() => {
    onSearchButtonClick({
      button_name: 'Saved Filters',
    });
  }, [onSearchButtonClick]);

  const wrappedToggleCrmMode = useCallback(() => {
    creatorCrmActions.toggleCrmMode();
    onSearchButtonClick({
      button_name: `Toggle CRM Mode: ${!isCrmMode ? 'CRM' : 'Search'}`,
    });
  }, [onSearchButtonClick, isCrmMode]);

  const onColumnSelectorOpen = useCallback(() => {
    onSearchButtonClick({
      button_name: 'Columns',
    });
  }, [onSearchButtonClick]);

  const onSortByOpen = useCallback(() => {
    onSearchButtonClick({
      button_name: 'Sort By Dropdown Opened',
    });
  }, [onSearchButtonClick]);

  const wrappedToggleSelectMode = useCallback(() => {
    creatorCrmActions.toggleSelectMode();
    onSearchButtonClick({
      button_name: `Toggle Select Mode: ${!isSelectMode ? 'On' : 'Off'}`,
    });
  }, [onSearchButtonClick, isSelectMode]);

  const {groupedActiveFilters, handleRemoveFilter} = useActiveFiltersBar();

  const groupedDisplayItems = useMemo(() => {
    const searchGroups =
      searchState && searchState.chips.length > 0
        ? groupedDisplayItemsFromSearchState(searchState)
        : [];
    const filterGroups = groupedDisplayItemsFromFilters(groupedActiveFilters);
    if (searchGroups.length > 0) {
      return [...searchGroups, ...filterGroups];
    }
    return filterGroups;
  }, [searchState, groupedActiveFilters]);

  const handleRemoveItem = useCallback(
    (item: DisplayItem) => {
      if (item.type === 'searchChip') {
        const chips = searchState?.chips ?? [];
        creatorCrmActions.updateSearchChips(
          chips.filter((c) => c !== item.value)
        );
      } else {
        handleRemoveFilter(item.data);
      }
    },
    [searchState?.chips, handleRemoveFilter]
  );

  const selectedPlatform = useMemo(
    () =>
      (
        filtersGroups.platforms.filters
          .platform as SingleSelection<BaseFilterOption>
      ).selected?.value,
    [filtersGroups]
  );

  const {isLoadingPrefetchedData} = usePrefetchRequiredData(brandId);
  const {brandCampaigns} = useBrandCampaignsQuery({
    brandId: brandId ?? '',
    refetchOnWindowFocus: false,
    states: ['accepting_proposals'],
  });

  const {
    data: creatorsSearchData,
    isFetching: isSearchCreatorsFetching,
    isRefetching: isSearchCreatorsRefetching,
    isLoading: isSearchCreatorsLoading,
    isFetched: isSearchCreatorsFetched,
    isError: isSearchCreatorsError,
    fetchNextPage,
    hasNextPage,
  } = useSearchCreatorCrmQuery({
    sessionId,
    paging,
    searchState,
    sorting,
    selectedBrandId: selectedBrandId || brandId,
    filterGroups: filtersGroupsSnapshot,
    isRestoring: isRestoringFromPersistenceState,
    disabled: isLoadingPrefetchedData,
    crm: isCrmMode,
  });

  useEffect(() => {
    creatorCrmActions.setTotalResults(
      creatorsSearchData.pagingParams.totalResults
    );
  }, [creatorsSearchData]);

  useEffect(() => {
    if (isSearchCreatorsError) {
      raiseFlashMessageError();
    }
  }, [isSearchCreatorsError]);

  useEffect(() => {
    if (isSearchCreatorsFetched && creatorsSearchData.searchSessionId) {
      creatorCrmActions.setSessionInformation(
        creatorsSearchData.searchSessionId
      );
    }
  }, [
    isSearchCreatorsLoading,
    isSearchCreatorsFetching,
    creatorsSearchData.searchSessionId,
  ]);

  useEffect(() => {
    if (!isSearchCreatorsFetching && creatorsSearchData.creators && sessionId) {
      const searchSessionId = creatorsSearchData?.searchSessionId || sessionId;
      trackRecruitmentResultsPresented.presented({
        recruitment_search_id: searchSessionId,
        recruitment_screen_name: getAnalyticsSearchScreenName(viewType),
        item_position: sorting?.field || '',
        item_identifier: sorting?.direction || '',
        creator_id: `${creatorsSearchData.creators.length} creators`,
      });
    }
  }, [
    sessionId,
    creatorsSearchData.creators,
    isSearchCreatorsFetching,
    sorting?.field,
    trackRecruitmentResultsPresented,
  ]);

  const searchDisplayCreators = useMemo(
    () => mapCreatorsForDisplay(creatorsSearchData.creators, selectedPlatform),
    [creatorsSearchData.creators, filtersGroups, selectedPlatform]
  );

  const noResults =
    creatorsSearchData.creators.length === 0 &&
    !isSearchCreatorsLoading &&
    !isSearchCreatorsFetching;

  const onSocialNetworkClick = useCallback(
    (buttonName: string, creatorId: string) =>
      onSearchButtonClick({
        button_name: buttonName,
        creator_id: creatorId,
      }),
    [onSearchButtonClick]
  );

  useEffect(() => {
    if (selectedBrandId && selectedBrandId !== brandId) {
      creatorCrmActions.updateUserLastBrandId(brandId);
      creatorCrmActions.resetAllFilters(true);
    }
  }, [selectedBrandId, brandId]);

  /* Callbacks */
  const logActionDrawerButtonClick = useCallback(
    (label: string) => {
      onSearchButtonClick({
        button_name: label,
        tab: 'Filters',
      });
    },
    [onSearchButtonClick]
  );

  const onPresentedOrDismissed = useCallback(
    (presentedOrDismissed: 'presented' | 'dismissed', name: string) => {
      if (presentedOrDismissed === 'presented') {
        trackSearchScreenEvent.presented({
          screen_name: getAnalyticsSearchScreenName(viewType),
          tab: name,
        });
      } else if (presentedOrDismissed === 'dismissed') {
        trackSearchScreenEvent.dismissed({
          screen_name: getAnalyticsSearchScreenName(viewType),
          tab: name,
        });
      }
    },
    [trackSearchScreenEvent]
  );

  const handleActionsDrawerClose = useCallback(
    (label: string) => {
      creatorCrmActions.closeFiltersDrawer();
      logActionDrawerButtonClick(label);
    },
    [logActionDrawerButtonClick]
  );

  const handleActionsDrawerClearAll = useCallback(
    (label: string) => {
      creatorCrmActions.resetFiltersAndCloseDrawer();
      logActionDrawerButtonClick(label);
    },
    [logActionDrawerButtonClick]
  );

  /* ====== Local state ====== */
  const [showNoActiveCampaignsModal, setShowNoActiveCampaignsModal] =
    useState<boolean>(false);
  const [activeActionKeyAndCreators, setActiveActionKeyAndCreators] =
    useState<ActiveActionKeyAndCreators | null>(null);

  const getSingleCreatorProfileId = () => {
    if (activeActionKeyAndCreators?.creators.length === 1) {
      return activeActionKeyAndCreators?.creators[0].id;
    }
    if (selectedCreators.size === 1) {
      return [...selectedCreators.values()][0].id;
    }
    return null;
  };

  const {profile} = useCreatorProfileQuery({
    brandId,
    profileId: getSingleCreatorProfileId(),
    enabled: !!getSingleCreatorProfileId(),
    include: ['current_brand_membership.labels'],
  });

  const getCreatorActionInitState = useMemo(() => {
    if (profile && profile.currentBrandMembership) {
      return {
        [CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS]:
          profile.currentBrandMembership.labels.map((label) => ({
            ...label,
            value: label.id,
            label: label.name,
            disabled: true,
          })),
      };
    }
    return undefined;
  }, [profile?.currentBrandMembership]);

  /* ====== Side effects ====== */

  // Overriding search params with the previous search params if persisted state is cleared by navigation
  useEffect(() => {
    const previousSearchParams =
      previousSearchParamsString != null
        ? new URLSearchParams(previousSearchParamsString)
        : null;
    if (
      !hasPersistedStateInSearchParams &&
      previousSearchParams?.has(PERSISTED_STATE_KEY)
    ) {
      searchParams.set(
        PERSISTED_STATE_KEY,
        previousSearchParams.get(PERSISTED_STATE_KEY) ?? ''
      );
      setSearchParams(searchParams, {replace: true});
    }
  }, [hasPersistedStateInSearchParams, brandId, previousSearchParamsString]);

  useEffect(() => {
    if (hasPersistedStateInSearchParams) {
      const restored = getPersistedStateFromSearchParams(searchParams);
      if (restored) {
        creatorCrmActions.initFromUrl({
          ...restored,
          // If the feature flag is off, force CRM mode regardless of persisted state
          isCrmMode: flags.crmSearchToggle ? restored.isCrmMode : true,
          content: {
            ...restored.content,
            sessionId,
            selectedCreators,
            paging,
          },
        });
      }
    }
    setIsRestoringFromPersistenceState(false);
  }, []);

  useEffect(() => {
    // RECRUITMENT_SEARCH_DETAIL analytics event
    if (isRestoringFromPersistenceState) {
      return;
    }

    try {
      const currentPersistedState =
        searchParams?.get(PERSISTED_STATE_KEY) ?? '';
      const previousSearchParams =
        previousSearchParamsString != null
          ? new URLSearchParams(previousSearchParamsString)
          : null;
      const previousPersistedState =
        previousSearchParams?.get(PERSISTED_STATE_KEY) ?? '';
      if (
        currentPersistedState &&
        previousPersistedState &&
        currentPersistedState !== previousPersistedState
      ) {
        let currentFilters = JSON.parse(
          decodeURIComponent(escape(atob(currentPersistedState)))
        );
        let previousFilters = JSON.parse(
          decodeURIComponent(escape(atob(previousPersistedState)))
        );
        currentFilters = {
          ...currentFilters,
          ...currentFilters.appliedFiltersGroups,
          appliedFiltersGroups: undefined,
        };
        previousFilters = {
          ...previousFilters,
          ...previousFilters.appliedFiltersGroups,
          appliedFiltersGroups: undefined,
        };

        const appliedFilters = getObjectDiff(previousFilters, currentFilters);
        Object.entries(appliedFilters).forEach(([path, filter]) => {
          trackRecruitmentSearchDetail.detail({
            recruitment_search_id: sessionId || '',
            recruitment_screen_name: getAnalyticsSearchScreenName(viewType),
            search_detail_id: path,
            search_detail_name: path,
            search_detail_value:
              typeof filter !== 'string' ? JSON.stringify(filter) : filter,
          });
        });
      }
    } catch {
      /* empty */
    }
  }, [searchParams, previousSearchParamsString, trackRecruitmentSearchDetail]);

  // Persisting state to search params on relevant changes
  useEffect(() => {
    const base64String = encodePersistedState(
      viewType,
      filtersGroupsSnapshot,
      sorting,
      brandId,
      tableColumns,
      filtersState,
      selectedSegmentId,
      isCrmMode
    );
    searchParams.set(PERSISTED_STATE_KEY, base64String);
    setSearchParams(searchParams, {replace: true});
  }, [
    viewType,
    filtersGroupsSnapshot,
    filtersState,
    sorting,
    brandId,
    tableColumns,
    selectedSegmentId,
    isCrmMode,
  ]);

  useEffect(() => {
    if (isMetadataFetched && metadata) {
      creatorCrmActions.setFiltersOptionsFromMetadata(metadata);
    }
  }, [isMetadataFetched, isMetadataError, metadata]);

  useEffect(() => {
    if (currentBrand?.name || agencyName) {
      creatorCrmActions.setCampaignHistoryBrandContext(
        currentBrand?.name ?? '',
        agencyName
      );
    }
  }, [currentBrand?.name, agencyName]);

  useEffect(() => {
    const fetchInvitationStatuses = async () => {
      setCreatorsInvitedStatus([]);
    };

    fetchInvitationStatuses();
  }, []);

  function raiseEmailCopiedMessage() {
    raiseFlashMessage({
      status: 'success',
      mode: 'light',
      message: (
        <Headline size="xs" width="100%" display="flex" justifyContent="center">
          {translate(`${TRANSLATION_PREFIX}.email-copied`)}
        </Headline>
      ),
      withCloseButton: false,
      style: {justifyContent: 'center'},
    });
  }

  /* ====== Event handlers ====== */

  /* Creator Action Modal events handlers */
  const onCloseCreatorAction = (
    reason: 'success' | 'cancelled',
    creators?: CreatorProps[],
    actionName?: string,
    result?: unknown
  ) => {
    if (reason === 'success') {
      if (
        activeActionKeyAndCreators?.actionKey ===
        CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS
      ) {
        raiseCreatorAddedToListSuccess(
          (result as MembershipLabel[]).filter(
            (label: MembershipLabel & {disabled?: boolean}) => !label.disabled
          )
        );
      }
      if (getSingleCreatorProfileId()) {
        queryClient.invalidateQueries([
          'creatorProfile',
          false,
          getSingleCreatorProfileId(),
          brandId,
          'current_brand_membership.labels',
        ]);
        queryClient.invalidateQueries([
          'creatorProfile',
          false,
          getSingleCreatorProfileId(),
          brandId,
        ]);
      }
      creatorCrmActions.deactivateSelectMode();
    }
    onSearchButtonClick({
      button_name: reason,
      tab: activeActionKeyAndCreators?.actionKey || '',
    });
    trackRecruitmentActionFlowEvent.ended({
      action_id: actionName || '',
      screen_name: getAnalyticsSearchScreenName(viewType),
      recruitment_search_id: sessionId || '',
      creator_id: JSON.stringify(
        creators ? creators.map((creator) => creator.id) : []
      ),
      reason,
      source: getAnalyticsSearchScreenName(viewType),
    });
    setActiveActionKeyAndCreators(null);
  };

  const handlePresentedOrDismissed = (
    presentedOrDismissed: 'presented' | 'dismissed',
    name: string,
    creators?: CreatorProps[]
  ) => {
    onPresentedOrDismissed(presentedOrDismissed, name);
    const creatorId =
      (creators?.length || 0) === 1
        ? (creators?.[0].id as string)
        : `${creators?.length || 0} creators`;
    if (presentedOrDismissed === 'presented') {
      trackRecruitmentActionFlowEvent.started({
        action_name: name,
        screen_name: getAnalyticsSearchScreenName(viewType),
        recruitment_search_id: sessionId || '',
        creator_id: creatorId,
        source: getAnalyticsSearchScreenName(viewType),
      });
    } else {
      trackRecruitmentActionFlowEvent.ended({
        action_id: name || '',
        screen_name: getAnalyticsSearchScreenName(viewType),
        recruitment_search_id: sessionId || '',
        creator_id: creatorId,
        reason: 'completed',
        source: getAnalyticsSearchScreenName(viewType),
      });
    }
  };

  /* NoActiveCampaignsModal events handlers */
  const onCloseNoActiveCampaignsModal = () => {
    setShowNoActiveCampaignsModal(false);
    onSearchButtonClick({
      button_name: 'dismiss',
      tab: 'no-active-campaigns',
    });
  };

  const onSaveToListClick = () => {
    setShowNoActiveCampaignsModal(false);
    setActiveActionKeyAndCreators({
      actionKey: CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS,
      creators: mapCreatorSearchResultsForAction([
        ...selectedCreators.values(),
      ]),
    });
    onSearchButtonClick({
      button_name: translate(
        `${TRANSLATION_PREFIX}.components.no-active-campaigns-modal.save-to-list-button`
      ),
      tab: 'no-active-campaigns',
    });
  };

  const onCreateCampaignClick = () => {
    setShowNoActiveCampaignsModal(false);
    searchParams.set('showNewCampaignWizardModal', 'true');
    setSearchParams(searchParams);

    onSearchButtonClick({
      button_name: translate(
        `${TRANSLATION_PREFIX}.components.no-active-campaigns-modal.create-campaign-button`
      ),
      tab: 'no-active-campaigns',
    });
  };

  /* Filters drawer events handlers */
  const onActionDrawerButtonClick = (label: string) => {
    onSearchButtonClick({
      button_name: label,
      creator_id: `${effectiveSelectedCount} creators`,
    });
  };

  /* SearchCreatorsDisplay event handlers */
  const onGridCreatorCardCtaClick = (
    actionKeyAndCreators: ActiveActionKeyAndCreators
  ) => {
    onAddToMyCreatorsAction(actionKeyAndCreators.creators);
  };

  const onTableCreatorsSelection = useCallback(
    (
      creators: CreatorSearchResult[],
      isSelected: boolean,
      selectMode: 'page' | 'individual'
    ) => {
      onSearchButtonClick({
        button_name: isSelected
          ? `Select ${selectMode} creators`
          : `Deselect ${selectMode} creators`,
        creator_id: `${creators.length} creators`,
      });
    },
    [onSearchButtonClick]
  );

  const onTableSortingSelection = useCallback(
    (sortField: string, direction: string) => {
      onSearchButtonClick({
        button_name: `Sorting: ${sortField || 'default'}`,
        tab: direction,
      });
    },
    [onSearchButtonClick]
  );

  const creatorsById = useMemo(
    () => new Map(creatorsSearchData.creators.map((c) => [c.id, c])),
    [creatorsSearchData.creators]
  );

  const {open: openMessagingPanel} = useMessagingPanel();

  const toMessageCreator = (creator: CreatorSearchResult): MessageCreator => ({
    id: creator.id,
    displayName: creator.fullName,
    imageUrl: getProfileImageUrl(creator.profileImage),
  });

  const messageCreators: MessageCreator[] = (() => {
    if (activeModal?.type !== 'message') {
      return [];
    }
    if (activeModal.creatorId) {
      const creator =
        creatorsById.get(activeModal.creatorId) ??
        selectedCreators.get(activeModal.creatorId);
      return creator ? [toMessageCreator(creator)] : [];
    }
    return [...selectedCreators.values()].map(toMessageCreator);
  })();

  const effectiveSelectedCount = isAllFilterMatchingSelected
    ? paging.totalResults
    : selectedCreators.size;

  const pageSelectionState = useMemo((): PageSelectionState => {
    const n = searchDisplayCreators.length;
    if (n === 0) {
      return PAGE_SELECTION_STATE.none;
    }
    const selectedCount = isAllFilterMatchingSelected
      ? n
      : searchDisplayCreators.filter((c) => selectedCreators.has(c.id)).length;
    if (selectedCount === n) {
      return PAGE_SELECTION_STATE.all;
    }
    if (selectedCount > 0) {
      return PAGE_SELECTION_STATE.partial;
    }
    return PAGE_SELECTION_STATE.none;
  }, [searchDisplayCreators, isAllFilterMatchingSelected, selectedCreators]);

  const onSelectionCreatorChange = useCallback(
    (id: string, isSelected: boolean) => {
      if (isAllFilterMatchingSelected) {
        if (!isSelected) {
          creatorCrmActions.setIsAllFilterMatchingSelected(false);
          creatorCrmActions.toggleSelectAllCreatorsOnPage(
            searchDisplayCreators,
            true
          );
        }
      }
      const creator = creatorsById.get(id);
      if (creator) {
        creatorCrmActions.selectCreator(creator, isSelected);
        onTableCreatorsSelection([creator], isSelected, 'individual');
      }
    },
    [isAllFilterMatchingSelected, creatorsById, onTableCreatorsSelection]
  );

  const onSelectAllPageChange = useCallback(
    (creators: typeof searchDisplayCreators, isSelected: boolean) => {
      if (!isSelected) {
        onTableCreatorsSelection(creators, isSelected, 'page');
        creatorCrmActions.clearSelectedCreators();
        return;
      }
      if (isAllFilterMatchingSelected) {
        return;
      }
      const creatorResults = creators
        .map((c) => creatorsById.get(c.id))
        .filter(Boolean) as CreatorSearchResult[];
      creatorCrmActions.toggleSelectAllCreatorsOnPage(
        creatorResults,
        isSelected
      );
      onTableCreatorsSelection(creatorResults, isSelected, 'page');
    },
    [creatorsById, onTableCreatorsSelection, isAllFilterMatchingSelected]
  );

  /* Creators Action Bar event handlers */
  const actionBarAvatarUrls = useMemo(() => {
    if (isAllFilterMatchingSelected) {
      return searchDisplayCreators.map((c) =>
        getProfileImageUrl(c.profileImage)
      );
    }
    return [...selectedCreators].map(([, creator]) =>
      getProfileImageUrl(creator.profileImage)
    );
  }, [isAllFilterMatchingSelected, searchDisplayCreators, selectedCreators]);

  const handleClearSelection = useCallback(() => {
    onSearchButtonClick({
      button_name: translate(
        `${TRANSLATION_PREFIX}.components.creators-actions-bar.button-clear-all`
      ),
      creator_id: `${effectiveSelectedCount} creators`,
    });
    creatorCrmActions.clearSelectedCreators();
  }, [onSearchButtonClick, effectiveSelectedCount]);

  const showSelectionBanner =
    effectiveSelectedCount > 0 &&
    pageSelectionState === PAGE_SELECTION_STATE.all &&
    viewType === VIEW_TYPE_TABLE;

  const selectionBannerElement = showSelectionBanner ? (
    <div className={styles.selectionBannerContainer}>
      <SelectionBanner
        isAllFilterMatchingSelected={isAllFilterMatchingSelected}
        currentPageCount={searchDisplayCreators.length}
        totalResultsCount={paging.totalResults}
        onSelectAll={() => {
          onSearchButtonClick({
            button_name: 'select_all',
          });
          creatorCrmActions.setIsAllFilterMatchingSelected(true);
        }}
        onClearSelection={handleClearSelection}
      />
    </div>
  ) : undefined;

  const isBulkActionLimitExceeded = effectiveSelectedCount > BULK_ACTION_LIMIT;

  const renderCreatorsActionBarHeader = useCallback(() => {
    if (isBulkActionLimitExceeded) {
      return (
        <NotificationBanner
          state={NotificationBannerState.WARNING}
          content={{
            type: NotificationContentType.CUSTOM_RENDER,
            customRender: () => (
              <Body size="md" color="currentcolor">
                {translate(
                  'Bulk actions are limited to {count} creators at a time. Add filters to narrow your selection.',
                  {count: BULK_ACTION_LIMIT.toLocaleString()}
                )}
              </Body>
            ),
          }}
        />
      );
    }
    return null;
  }, [isBulkActionLimitExceeded]);

  const renderCreatorsActionBar = () => (
    <CreatorsActionsBar
      avatarUrls={actionBarAvatarUrls}
      selectedCount={effectiveSelectedCount}
      actionItems={tableActionsBarItems}
      closeButtonOnClick={handleClearSelection}
      closeButtonText={translate(
        `${TRANSLATION_PREFIX}.components.creators-actions-bar.button-clear-all`
      )}
      closeButtonProps={{
        appearance: 'neutral',
        mode: 'plain',
        size: 'medium',
        className: styles.creatorsActionsBarCloseButton,
      }}
      onPresentedOrDismissed={onPresentedOrDismissed}
      renderHeader={renderCreatorsActionBarHeader}
    />
  );

  const onAddToMyCreatorsAction = useCallback(
    (creators: CreatorProps[]) => {
      setActiveActionKeyAndCreators({
        actionKey: CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS,
        creators,
      });

      onSearchButtonClick({
        button_name: translate(
          `${TRANSLATION_PREFIX}.components.creator-action.add-to-my-creators.tooltip`
        ),
        tab: '',
      });
    },
    [trackSearchButtonEvent]
  );

  const bulkThreadsMutation = useBulkThreads();
  const bulkThreadsAllMutation = useBulkThreadsAll();
  const recruitToCrmMutation = useRecruitToCrm();
  const recruitAllToCrmMutation = useRecruitAllToCrm();

  const canShowRecruitToCrm =
    flags.crmSearchToggle && !isCrmMode && Boolean(agency?.id);

  const searchModeDisabledActions = useMemo(
    () =>
      isSearchMode
        ? new Set([
            CREATOR_ACTION_KEYS.MESSAGE_CREATOR,
            CREATOR_ACTION_KEYS.SEND_EMAIL,
            CREATOR_ACTION_KEYS.ADD_TO_CAMPAIGN,
          ] as CreatorActionKey[])
        : undefined,
    [isSearchMode]
  );

  const showRecruitToCrmSuccess = useCallback(() => {
    raiseFlashMessage({
      status: 'success',
      message: translate('creator-recruited-to-crm'),
    });
  }, []);

  const showRecruitToCrmError = useCallback(() => {
    raiseFlashMessageError({
      title: translate('creator-recruit-to-crm-error'),
      subtitle: '',
    });
  }, []);

  const handleRecruitToCrm = useCallback(
    async (creator: CreatorCardProps) => {
      const creatorId = creator.id;
      const agencyId = agency?.id;
      let isError = false;

      if (!agencyId) {
        showRecruitToCrmError();
        return;
      }

      try {
        const response = await recruitToCrmMutation.mutateAsync({
          agencyId,
          creatorIds: [creatorId],
        });

        const result = response.results.profiles[0];
        if (result?.success) {
          showRecruitToCrmSuccess();
          handlePresentedOrDismissed(
            'dismissed',
            EVENT_NAMES.RECRUIT_TO_CRM,
            [creator].map(mapCreatorCardPropsForAction)
          );
        } else {
          isError = true;
        }
      } catch (error) {
        isError = true;
      } finally {
        if (isError) {
          showRecruitToCrmError();
        }
      }
    },
    [
      recruitToCrmMutation,
      agency?.id,
      showRecruitToCrmSuccess,
      showRecruitToCrmError,
    ]
  );

  const handleBulkRecruitToCrm = useCallback(
    async (creators: CreatorSearchResult[]) => {
      const agencyId = agency?.id;

      if (!agencyId) {
        showRecruitToCrmError();
        return;
      }

      if (isAllFilterMatchingSelected && sessionId) {
        try {
          await recruitAllToCrmMutation.mutateAsync({
            agencyId,
            searchSessionId: sessionId,
          });
          raiseFlashMessage({
            status: 'success',
            message: translate('creator-bulk-recruit-to-crm-all-enqueued'),
          });
          trackRecruitmentActionFlowEvent.ended({
            action_id: EVENT_NAMES.RECRUIT_TO_CRM,
            screen_name: getAnalyticsSearchScreenName(viewType),
            recruitment_search_id: sessionId || '',
            creator_id: `${effectiveSelectedCount} creators`,
            reason: 'completed',
            source: getAnalyticsSearchScreenName(viewType),
          });
          creatorCrmActions.deactivateSelectMode();
        } catch {
          showRecruitToCrmError();
        }
        return;
      }

      const creatorIds = creators.map((c) => c.id);

      try {
        const response = await recruitToCrmMutation.mutateAsync({
          agencyId,
          creatorIds,
        });

        const successCount = response.results.profiles.filter(
          (r: RecruitToCrmProfileResultItem) => r.success
        ).length;
        const errorCount = response.results.profiles.filter(
          (r: RecruitToCrmProfileResultItem) => !r.success
        ).length;

        if (errorCount === 0) {
          raiseFlashMessage({
            status: 'success',
            message: translate('creator-bulk-recruited-to-crm-success', {
              count: successCount,
            }),
          });
        } else if (successCount > 0) {
          raiseFlashMessage({
            status: 'warning',
            message: translate('creator-bulk-recruit-to-crm-partial', {
              successCount,
              errorCount,
            }),
          });
        } else {
          showRecruitToCrmError();
        }

        handlePresentedOrDismissed(
          'dismissed',
          EVENT_NAMES.RECRUIT_TO_CRM,
          mapCreatorSearchResultsForAction(creators)
        );
        if (successCount > 0) {
          creatorCrmActions.clearSelectedCreators();
        }
      } catch (error) {
        showRecruitToCrmError();
      }
    },
    [
      agency?.id,
      isAllFilterMatchingSelected,
      sessionId,
      recruitAllToCrmMutation,
      recruitToCrmMutation,
      showRecruitToCrmError,
    ]
  );

  const onAddToCampaignAction = useCallback(
    (creators: CreatorProps[]) => {
      if (brandCampaigns.length === 0) {
        setShowNoActiveCampaignsModal(true);
      } else {
        setActiveActionKeyAndCreators({
          actionKey: CREATOR_ACTION_KEYS.ADD_TO_CAMPAIGN,
          creators,
        });
      }
      onSearchButtonClick({
        button_name:
          brandCampaigns.length === 0
            ? translate(
                `${TRANSLATION_PREFIX}.components.no-active-campaigns-modal.save-to-list-button`
              )
            : translate(
                `${TRANSLATION_PREFIX}.components.creator-action.add-to-campaign.tooltip`
              ),
        tab: '',
      });
    },
    [brandCampaigns.length, onSearchButtonClick]
  );

  const onCreateAndShareListAction = useCallback(
    (creators: CreatorProps[]) => {
      setActiveActionKeyAndCreators({
        actionKey: CREATOR_ACTION_KEYS.CREATE_AND_SHARE_LIST,
        creators,
      });

      onSearchButtonClick({
        button_name: 'create-and-share-list',
        tab: '',
      });
    },
    [onSearchButtonClick]
  );

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (scrollContainer) {
        return;
      }
      if (event.currentTarget) {
        setScrollContainer(event.currentTarget);
      }
    },
    [scrollContainer]
  );

  const handleSingleCreatorAction = useCallback(
    (creatorId: string, action: (creators: CreatorProps[]) => void) => {
      const creator = creatorsById.get(creatorId);
      if (creator) {
        action(mapCreatorSearchResultsForAction([creator]));
      }
    },
    [creatorsById]
  );

  const handleCreatorAction = useCallback(
    (creatorId: string, actionKey: CreatorActionKey) => {
      const creator = creatorsById.get(creatorId);
      if (
        isOffPlatformCreator(creator) &&
        disabledActionKeysForCreator(creator).has(actionKey)
      ) {
        return;
      }

      switch (actionKey) {
        case CREATOR_ACTION_KEYS.CREATE_AND_SHARE_LIST:
          handleSingleCreatorAction(creatorId, onCreateAndShareListAction);
          break;
        case CREATOR_ACTION_KEYS.ADD_TO_CAMPAIGN:
          handleSingleCreatorAction(creatorId, onAddToCampaignAction);
          break;
        case CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS:
          handleSingleCreatorAction(creatorId, onAddToMyCreatorsAction);
          break;
        case CREATOR_ACTION_KEYS.MESSAGE_CREATOR:
        case CREATOR_ACTION_KEYS.SEND_EMAIL:
          openMessagingPanel(creatorId);
          break;
        case CREATOR_ACTION_KEYS.ADD_NOTE:
          setActiveModal({type: 'note', creatorId});
          break;
        default:
          break;
      }
    },
    [
      creatorsById,
      handleSingleCreatorAction,
      openMessagingPanel,
      onCreateAndShareListAction,
      onAddToCampaignAction,
      onAddToMyCreatorsAction,
    ]
  );

  const columns = useCreatorsTableColumns({
    selectedPlatform: selectedPlatform ?? 'all',
    onSelectionCreatorChange,
    onSocialNetworkClick,
    currentPageCreators: searchDisplayCreators,
    pageSelectionState,
    onSelectAllPageChange,
    tableColumns,
    onCreatorAction: handleCreatorAction,
    disabledActionKeys: searchModeDisabledActions,
    isSearchMode,
  });

  const {
    columnConfigs,
    pinnedColumns,
    columnOrder,
    visibleColumns,
    handleColumnVisibilityChange,
    handleColumnPinChange,
    columnsWithSticky,
  } = useCreatorTableColumnsConfig({
    selectedPlatform: selectedPlatform ?? 'all',
    columns,
    stickyClassNames: {
      pinnedHeaderCell: stickyStyles.pinnedHeaderCell,
      pinnedBodyCell: stickyStyles.pinnedBodyCell,
      pinnedColumnShadow: stickyStyles.pinnedColumnShadow,
    },
    isSearchMode,
  });

  const handleMessageCreatorsClick = useCallback(() => {
    if (effectiveSelectedCount === 0) {
      return;
    }
    onSearchButtonClick({
      button_name: translate('Send message'),
      tab: '',
    });

    setActiveModal({type: 'message', creatorId: null});
  }, [effectiveSelectedCount, onSearchButtonClick]);

  const handleMessageCreatorsSubmit = useCallback(
    async (subject: string, body: string) => {
      if (!brandId || !agency?.id) {
        console.error('Missing brandId or agencyId for message creators', {
          brandId,
          agencyId: agency?.id,
        });
        return;
      }

      const initialMessage = `**${subject}**\n\n${body}`;

      try {
        const creatorIds = singleCreatorActionId
          ? [singleCreatorActionId]
          : Array.from(selectedCreators.keys());

        if (
          !singleCreatorActionId &&
          isAllFilterMatchingSelected &&
          sessionId
        ) {
          await bulkThreadsAllMutation.mutateAsync({
            agencyId: agency.id,
            brandId,
            searchSessionId: sessionId,
            initialMessage,
          });
          creatorCrmActions.deactivateSelectMode();
        } else {
          await bulkThreadsMutation.mutateAsync({
            agencyId: agency.id,
            brandId,
            creatorIds,
            initialMessage,
          });

          if (!singleCreatorActionId) {
            creatorCrmActions.deactivateSelectMode();
          }
        }
      } catch (err: unknown) {
        let subtitle = translate('Please try again');

        if (isAxiosError(err)) {
          const errorData = err.response?.data as
            | {error?: string; unauthorized_ids?: string[]}
            | undefined;
          const errorMessage = errorData?.error;
          const unauthorizedIds = errorData?.unauthorized_ids;

          if (errorMessage) {
            subtitle = errorMessage;
            if (unauthorizedIds && unauthorizedIds.length > 0) {
              subtitle += `. (${translate('{count} creators not accessible', {count: unauthorizedIds.length})})`;
            }
          }
        }

        raiseFlashMessageError({
          title: translate('Failed to send message'),
          subtitle,
        });
        throw err;
      }
    },
    [
      brandId,
      agency?.id,
      sessionId,
      isAllFilterMatchingSelected,
      selectedCreators,
      singleCreatorActionId,
      bulkThreadsMutation,
      bulkThreadsAllMutation,
    ]
  );

  /* ====== Table Creators Actions Bar action items ====== */
  const gateOffPlatformBulkActions = shouldGateOffPlatformBulkActions(
    selectedCreators.values(),
    isAllFilterMatchingSelected
  );

  const tableActionsBarItems: ActionItem[] = useMemo(() => {
    const items: ActionItem[] = [
      {
        id: CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS,
        icon: 'Actions-Bookmark',
        tooltip: translate(
          `${TRANSLATION_PREFIX}.components.creator-action.add-to-my-creators.tooltip`
        ),
        onClick: () =>
          onAddToMyCreatorsAction(
            isAllFilterMatchingSelected
              ? []
              : mapCreatorSearchResultsForAction([...selectedCreators.values()])
          ),
        disabled: isBulkActionLimitExceeded,
      },
      {
        id: CREATOR_ACTION_KEYS.CREATE_AND_SHARE_LIST,
        icon: 'Actions-Share',
        tooltip: translate('Share as list'),
        onClick: () =>
          onCreateAndShareListAction(
            mapCreatorSearchResultsForAction([...selectedCreators.values()])
          ),
        disabled: isBulkActionLimitExceeded,
      },
    ];

    if (!isSearchMode) {
      items.push(
        {
          id: 'message-creators',
          icon: 'Navigation-Mail',
          tooltip: translate('Message creators', {
            count: effectiveSelectedCount,
          }),
          onClick: handleMessageCreatorsClick,
          disabled: effectiveSelectedCount === 0 || isBulkActionLimitExceeded,
        },
        {
          id: CREATOR_ACTION_KEYS.ADD_TO_CAMPAIGN,
          icon: 'Navigation-Megaphone',
          tooltip: translate(
            `${TRANSLATION_PREFIX}.components.creator-action.add-to-campaign.tooltip`
          ),
          onClick: () =>
            onAddToCampaignAction(
              isAllFilterMatchingSelected
                ? []
                : mapCreatorSearchResultsForAction([
                    ...selectedCreators.values(),
                  ])
            ),
          disabled: isBulkActionLimitExceeded,
        }
      );
    }

    if (canShowRecruitToCrm) {
      items.push({
        id: 'recruit-to-crm',
        icon: RECRUIT_TO_CRM_ACTION.icon,
        tooltip: translate(RECRUIT_TO_CRM_ACTION.bulkTooltipKey),
        onClick: () => handleBulkRecruitToCrm([...selectedCreators.values()]),
        disabled: isBulkActionLimitExceeded,
      });
    }

    return items.filter(
      (item) =>
        !(gateOffPlatformBulkActions && isOffPlatformBulkGatedAction(item.id))
    );
  }, [
    selectedCreators,
    isAllFilterMatchingSelected,
    effectiveSelectedCount,
    isBulkActionLimitExceeded,
    onAddToMyCreatorsAction,
    onCreateAndShareListAction,
    onAddToCampaignAction,
    handleMessageCreatorsClick,
    canShowRecruitToCrm,
    handleBulkRecruitToCrm,
    isSearchMode,
    gateOffPlatformBulkActions,
  ]);

  const getCreatorCardCtaProps = () => {
    const isCsm: boolean = dashboardUser.hasPlatformAdminRole;

    const getButtonText = (creator: CreatorCardProps) => {
      const creatorStatus = creatorsInvitedStatus.find(
        (item) => item.id === creator.id
      );

      if (isOffPlatformCreator(creator)) {
        return isCsm
          ? translate('components.creator-card.invite-to-campaign')
          : translate('components.creator-card.add-to-list');
      }

      if (isCsm) {
        if (creatorStatus && creatorStatus.invited) {
          return (
            <span className={styles.invitationSent}>
              <Icon name="Actions-Accept" size="medium" appearance="disabled" />
              {translate('components.creator-card.invitation-sent')}
            </span>
          );
        }
        return translate('components.creator-card.invite-to-campaign');
      }

      return translate('components.creator-card.add-to-list');
    };

    const isButtonDisabled = (creator: CreatorCardProps) => {
      if (isSearchMode && isCsm) {
        return true;
      }
      const creatorStatus = creatorsInvitedStatus.find(
        (item) => item.id === creator.id
      );
      return isCsm && creatorStatus && creatorStatus.invited;
    };

    return {
      buttonText: (creator: CreatorCardProps) => getButtonText(creator),
      onClick: (event: React.MouseEvent, creator: CreatorCardProps) => {
        event.stopPropagation();
        event.preventDefault();

        if (isCsm) {
          onAddToCampaignAction([mapCreatorCardPropsForAction(creator)]);
        } else {
          onAddToMyCreatorsAction([mapCreatorCardPropsForAction(creator)]);
        }
      },
      isDisabled: (creator: CreatorCardProps) => isButtonDisabled(creator),
    };
  };

  /* ====== Grid Creator Card Menu action items callBack ====== */
  const createCreatorCardOverflowMenuItems = useCallback(
    async (creator: CreatorCardProps): Promise<CreatorMenuItemProps[]> => {
      return [
        {
          iconName: 'Navigation-Copy',
          title: translate('components.creator-card.overflow-menu.copy-email'),
          onClick: () => {
            copy(creator.email);
            raiseEmailCopiedMessage();
            onSearchButtonClick({
              button_name: translate(
                'components.creator-card.overflow-menu.copy-email'
              ),
              creator_id: creator.id,
            });
          },
        },
        {
          iconName: 'Actions-Bookmark',
          title: translate('components.creator-card.overflow-menu.add-to-list'),
          onClick: () => {
            onAddToMyCreatorsAction([mapCreatorCardPropsForAction(creator)]);
          },
        },
        canCreateCollaboration &&
        !isSearchMode &&
        !isOffPlatformCreator(creator)
          ? {
              iconName: 'Navigation-Messages',
              title: translate(
                'components.creator-card.overflow-menu.open-thread'
              ),
              onClick: () => {
                openMessagingPanel(creator.id);
              },
            }
          : null,
        canShowRecruitToCrm && !isOffPlatformCreator(creator)
          ? {
              iconName: RECRUIT_TO_CRM_ACTION.icon,
              title: translate(RECRUIT_TO_CRM_ACTION.overflowMenuTitleKey),
              onClick: () => {
                handleRecruitToCrm(creator);
              },
            }
          : null,
      ].filter(Boolean) as CreatorMenuItemProps[];
    },
    [
      canCreateCollaboration,
      canShowRecruitToCrm,
      isSearchMode,
      openMessagingPanel,
      navigate,
      onAddToMyCreatorsAction,
      handleRecruitToCrm,
      trackSearchButtonEvent,
      viewType,
    ]
  );

  return (
    <Page
      id="creator-crm"
      className={styles.container}
      childrenClassName={styles.content}
    >
      <div
        className={clsx(styles.actionHeaderCollapsible, {
          [styles.collapsed]: shouldHideHeader,
        })}
      >
        <div className={styles.actionHeaderInner}>
          <div
            className={clsx(styles.actionHeaderContainer, {
              [styles.hidden]: shouldHideHeader,
            })}
          >
            <div className={styles.actionHeader}>
              <div className={styles.searchAndFiltersBar}>
                <SearchControls
                  totalResults={paging.totalResults}
                  viewType={viewType}
                  onActionDrawerButtonClick={onActionDrawerButtonClick}
                  onSegmentDeleted={wrappedOnSegmentDeleted}
                  onSelectSegment={wrappedSelectSegment}
                  onSegmentsButtonOpen={onSegmentsButtonOpen}
                  onSortByOpen={onSortByOpen}
                  sorting={sorting}
                  selectedPlatform={selectedPlatform}
                  segments={segments}
                  activeSegment={activeSegment ?? activeCampaignSegment}
                  activeCampaignSegment={activeCampaignSegment}
                  isLoadingSegments={isLoadingSegments}
                  organizationName={agencyName}
                  isSelectMode={isSelectMode}
                  onToggleSelectMode={wrappedToggleSelectMode}
                  {...(flags.crmSearchToggle
                    ? {
                        isCrmMode,
                        onToggleCrmMode: wrappedToggleCrmMode,
                        isSearchMode,
                      }
                    : {})}
                  columnSelector={
                    viewType === VIEW_TYPE_TABLE ? (
                      <ColumnSelector
                        testID="creator-crm-column-selector"
                        columns={columnConfigs}
                        visibleColumns={
                          visibleColumns as Record<string, boolean>
                        }
                        pinnedColumns={pinnedColumns}
                        canPinColumns={false}
                        onColumnVisibilityChange={handleColumnVisibilityChange}
                        onColumnPinChange={handleColumnPinChange}
                        onOpen={onColumnSelectorOpen}
                      />
                    ) : undefined
                  }
                />
                <ActiveFiltersBar
                  groupedDisplayItems={groupedDisplayItems}
                  activeSegment={activeSegment ?? activeCampaignSegment}
                  onRemoveItem={handleRemoveItem}
                  onSaveFilters={openSegmentManagementModal}
                  onClearAll={() => {
                    creatorCrmActions.updateSearchChips([]);
                    creatorCrmActions.resetAllFilters();
                    onSearchButtonClick({
                      button_name: translate('Clear All'),
                      tab: 'Filters',
                    });
                  }}
                />
              </div>
              <Headline size="lg" className={styles.headline}>
                {translate('Agency Creators', {
                  name: agencyName,
                })}
              </Headline>
              {selectionBannerElement}
            </div>
          </div>
        </div>
      </div>
      <ActionsDrawer
        canSaveSegment={canSaveSegment}
        onClose={handleActionsDrawerClose}
        onClearAll={handleActionsDrawerClearAll}
        onSaveAsSegment={openSegmentManagementModal}
        onPresentedOrDismissed={onPresentedOrDismissed}
        isSearchMode={isSearchMode}
      />
      <CreatorAction
        creators={activeActionKeyAndCreators?.creators || []}
        activeCreatorAction={activeActionKeyAndCreators?.actionKey || null}
        close={onCloseCreatorAction}
        onPresentedOrDismissed={handlePresentedOrDismissed}
        initState={getCreatorActionInitState}
        selectedCampaignId={activeCampaignSegment?.campaignId}
        isAllFilterMatchingSelected={isAllFilterMatchingSelected}
        searchSessionId={sessionId}
      />
      <SegmentManagementModal
        open={segmentModalOpen}
        onClose={closeSegmentManagementModal}
        currentFilters={currentFiltersForSegment}
        existingSegments={segments}
        groupedDisplayItems={groupedDisplayItems}
        onSegmentSaved={wrappedSegmentSaved}
        activeSegment={activeSegment}
        currentUserId={dashboardUser?.id ?? null}
        brandName={currentBrand?.name ?? undefined}
        organizationName={agencyName}
      />
      <NoActiveCampaignsModal
        open={showNoActiveCampaignsModal}
        close={onCloseNoActiveCampaignsModal}
        onSaveToListClick={onSaveToListClick}
        onCreateCampaignClick={onCreateCampaignClick}
      />
      <FloatingPopperBar
        className={styles.floatingPopperBar}
        isOpen={effectiveSelectedCount > 0}
        placement="bottom"
        renderContent={renderCreatorsActionBar}
      >
        <SearchCreatorsDisplay
          viewType={viewType}
          isHeaderHidden={shouldHideHeader}
          searchCreatorsQueryDisabled={isLoadingPrefetchedData}
          onSortingSelection={onTableSortingSelection}
          creatorCardOverflowMenuEnabled={dashboardUser.hasPlatformAdminRole}
          creatorCardOverflowMenuItemsCallBack={
            createCreatorCardOverflowMenuItems
          }
          creatorCardCtaProps={getCreatorCardCtaProps()}
          tableColumns={columnsWithSticky}
          tableColumnOrder={columnOrder}
          tableVisibleColumns={visibleColumns as Record<string, boolean>}
          searchDisplayCreators={searchDisplayCreators}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isSearchCreatorsLoading={isSearchCreatorsLoading}
          isSearchCreatorsFetching={isSearchCreatorsFetching}
          isSearchCreatorsRefetching={isSearchCreatorsRefetching}
          loadedPages={creatorsSearchData.loadedPages}
          sessionId={sessionId}
          noResults={noResults}
          onSocialNetworkClick={onSocialNetworkClick}
          onScroll={handleScroll}
          isSelectMode={isSelectMode}
          onSelectChange={onSelectionCreatorChange}
          isSearchMode={isSearchMode}
        />
      </FloatingPopperBar>

      <MessageCreatorsModal
        open={activeModal?.type === 'message'}
        onClose={() => {
          setActiveModal(null);
        }}
        creators={messageCreators}
        totalCreatorCount={effectiveSelectedCount}
        onSend={handleMessageCreatorsSubmit}
        subjectPlaceholder={translate(
          'e.g. New campaign opportunity from {name}',
          {name: agencyName}
        )}
        bodyPlaceholder={translate(
          "e.g. Hi {creator_name}, we have a campaign we think you'd love..."
        )}
        onPlaceholderInsert={(placeholder) =>
          onSearchButtonClick({
            button_name: `insert_placeholder_${placeholder}`,
          })
        }
        onStepChange={(step) =>
          onSearchButtonClick({button_name: `message_creators_${step}`})
        }
      />
      <AddNoteModal
        open={activeModal?.type === 'note'}
        onClose={() => {
          setActiveModal(null);
        }}
        creatorId={singleCreatorActionId}
        creatorName={
          singleCreatorActionId
            ? creatorsById.get(singleCreatorActionId)?.fullName
            : undefined
        }
      />
    </Page>
  );
}

function CreatorCrmWrapper() {
  const brandId = useBrandId();
  const {subscription} = useSubscriptionQuery({
    brandId,
  });

  if (!brandId || !subscription.loaded) {
    return null;
  }
  return <CreatorCrm />;
}

export default CreatorCrmWrapper;
